import api from '../api/apiClient';
import {
  AuthResponse,
  LoginPayload,
  RegisterPayload,
  ResetPasswordPayload,
  ChangePasswordPayload,
  User,
  VerifyCodePayload,
  VerifyCodeResponse,
} from '../../types/auth';

import { requestRefresh } from './refreshCoordinator';

const AUTH_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user_info';
const EMAIL_VERIFICATION_KEY = 'email_verification';
const SESSION_EXPIRY_KEY = 'session_expiry';

// 인증 토큰 관련 유틸리티 함수
const setAuthToken = (token: string): void => {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
};

const getAuthToken = (): string | null => {
  return localStorage.getItem(AUTH_TOKEN_KEY);
};

const removeAuthToken = (): void => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
};

// 새로고침 토큰 관련 유틸리티 함수
const setRefreshToken = (token: string): void => {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
};

const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

const removeRefreshToken = (): void => {
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

// 세션 만료 시간 관련 유틸리티 함수
const setSessionExpiry = (expiryDate: Date): void => {
  localStorage.setItem(SESSION_EXPIRY_KEY, expiryDate.toISOString());
};

const getSessionExpiry = (): Date | null => {
  const expiryStr = localStorage.getItem(SESSION_EXPIRY_KEY);
  return expiryStr ? new Date(expiryStr) : null;
};

const removeSessionExpiry = (): void => {
  localStorage.removeItem(SESSION_EXPIRY_KEY);
};

// 사용자 정보 관련 유틸리티 함수
const setUserInfo = (user: User): void => {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

const getUserInfo = (): User | null => {
  const userString = localStorage.getItem(USER_KEY);
  if (userString) {
    try {
      return JSON.parse(userString) as User;
    } catch (error) {
      console.error('Failed to parse user info:', error);
      return null;
    }
  }
  return null;
};

const removeUserInfo = (): void => {
  localStorage.removeItem(USER_KEY);
};

// 이메일 인증 상태 관련 유틸리티 함수
const setEmailVerification = (email: string, verified: boolean): void => {
  const verificationData = {
    email,
    verified,
    timestamp: new Date().toISOString(),
  };
  localStorage.setItem(EMAIL_VERIFICATION_KEY, JSON.stringify(verificationData));
};

const getEmailVerification = (email: string): boolean => {
  const verificationString = localStorage.getItem(EMAIL_VERIFICATION_KEY);
  if (verificationString) {
    try {
      const verification = JSON.parse(verificationString) as {
        email: string;
        verified: boolean;
        timestamp: string;
      };
      // 24시간 내 인증된 이메일인지 확인
      const verificationTime = new Date(verification.timestamp).getTime();
      const currentTime = new Date().getTime();
      const isValid = currentTime - verificationTime < 24 * 60 * 60 * 1000; // 24시간

      return verification.email === email && verification.verified && isValid;
    } catch (error) {
      console.error('Failed to parse email verification info:', error);
      return false;
    }
  }
  return false;
};

const removeEmailVerification = (): void => {
  localStorage.removeItem(EMAIL_VERIFICATION_KEY);
};

// 인증 서비스 함수들
const authService = {
  // 로그인
  login: async (credentials: LoginPayload): Promise<AuthResponse> => {
    try {
      const response = await api.post<AuthResponse>('/auth/login', credentials);

      // 응답으로 받은 토큰과 사용자 정보 저장
      if (response.success && response.token) {
        setAuthToken(response.token);

        if (response.refreshToken) {
          setRefreshToken(response.refreshToken);
        } else {
          console.warn('authService.login: 응답에 리프레시 토큰이 없음');
        }

        if (response.user) {
          setUserInfo(response.user);
        } else {
          console.warn('authService.login: 응답에 사용자 정보가 없음');
        }

        // 세션 만료 시간 설정 (기본 1시간)
        const expiresIn = response.expiresIn || 3600;
        const sessionExpiry = new Date(Date.now() + expiresIn * 1000);
        setSessionExpiry(sessionExpiry);
      } else {
        console.warn('authService.login: 로그인 성공 응답이지만 토큰이 없음:', response);
      }

      return response;
    } catch (error) {
      console.error('authService.login: 로그인 과정에서 오류 발생:', error);
      throw error;
    }
  },

  // 등록
  register: async (userData: RegisterPayload): Promise<AuthResponse> => {
    try {
      // 회원가입 API 호출
      const response = await api.post<AuthResponse>('/auth/register', userData);

      // 응답 확인
      if (!response.success) {
        console.error('authService: 회원가입 응답이 성공 상태가 아님', response.message);
        throw new Error(response.message || '회원가입에 실패했습니다.');
      }

      // 회원가입 직후 자동 로그인 시 토큰과 사용자 정보 저장
      if (response.token) {
        setAuthToken(response.token);
        if (response.refreshToken) {
          setRefreshToken(response.refreshToken);
        } else {
          console.warn('authService: 회원가입 응답에 리프레시 토큰이 없음');
        }

        if (response.user) {
          setUserInfo(response.user);
        } else {
          console.warn('authService: 회원가입 응답에 사용자 정보가 없음');
        }

        // 세션 만료 시간 설정 (기본 1시간)
        const expiresIn = response.expiresIn || 3600;
        const sessionExpiry = new Date(Date.now() + expiresIn * 1000);
        setSessionExpiry(sessionExpiry);
      } else {
        console.warn('authService: 회원가입 응답에 토큰이 없음');
      }

      return response;
    } catch (error) {
      console.error('authService: 회원가입 처리 중 오류 발생', error);
      throw error;
    }
  },

  // 로그아웃
  logout: async (): Promise<void> => {
    // ⛔ **저장소를 먼저 비운다** — 최종 심사 [F-11].
    //
    //    종전에는 서버 통보를 `await` 한 뒤 `finally` 에서 지웠다. 그런데 서버가
    //    응답하지 않으면 제한 시간이 120초다(`apiClient.ts:63`). 그동안 토큰이 남아 있어
    //    「토큰은 있는데 인증 안 됨」 상태가 이어지고, 화면이 3초마다 새로고침을 건다.
    //    서버가 죽었을 때 «다시 로그인» 이 실질적으로 취소되던 원인이다.
    //
    //    로그아웃의 본질은 **이 기기에서 자격을 버리는 것**이다. 서버 통보는 부수적이고,
    //    실패해도 사용자는 이미 나간 것이 맞다.
    removeAuthToken();
    removeRefreshToken();
    removeUserInfo();
    removeEmailVerification();
    removeSessionExpiry();

    try {
      // 서버 로그아웃 엔드포인트 호출 (선택적) — 실패해도 위 삭제는 이미 끝났다
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout failed on server:', error);
    }
  },

  // 현재 사용자 정보 가져오기
  getCurrentUser: async (): Promise<User> => {
    // 로컬 저장소에서 사용자 정보 확인
    const user = getUserInfo();
    if (user) {
      return user;
    }

    // 로컬에 사용자 정보가 없으면 서버에서 가져오기
    const response = await api.get<{ success: boolean; data: User }>('/auth/me');
    if (response.success && response.data) {
      setUserInfo(response.data);
      return response.data;
    }
    throw new Error('사용자 정보를 가져올 수 없습니다.');
  },

  // 서버에서 최신 사용자 정보 강제 조회 (캐시 무시)
  fetchCurrentUser: async (): Promise<User> => {
    const response = await api.get<{ success: boolean; data: User }>('/auth/me');
    if (response.success && response.data) {
      setUserInfo(response.data);
      return response.data;
    }
    throw new Error('사용자 정보를 가져올 수 없습니다.');
  },

  // 토큰 새로고침
  refreshToken: async (): Promise<{
    success: boolean;
    token: string;
    refreshToken?: string;
    expiresIn?: number;
    user?: User;
  }> => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      console.error('authService.refreshToken: 리프레시 토큰이 없음');
      throw new Error('No refresh token available');
    }

    // ⛔ 재발급은 **조정자 한 곳**에서만 나간다 [부당한 로그아웃 제거 1단계].
    //    종전에는 이 함수가 직접 `/auth/refresh` 를 불렀다. 그래서 axios 가로채기·
    //    fetch 경로와 합쳐 **셋이 서로를 모른 채** 각자 갱신했고,
    //    실패 이유를 가리지 못해 서버 장애에도 로그아웃했다.
    const outcome = await requestRefresh();

    if (outcome.kind !== 'ok') {
      // 이유는 부른 쪽이 판단한다. 여기서는 로그아웃하지 않는다.
      throw new Error(
        outcome.kind === 'unavailable'
          ? '서버에 연결할 수 없습니다.'
          : '인증이 만료되었습니다.',
      );
    }

    const 저장된만료 = localStorage.getItem(SESSION_EXPIRY_KEY);
    const expiresIn = 저장된만료
      ? Math.max(0, Math.round((new Date(저장된만료).getTime() - Date.now()) / 1000))
      : undefined;

    let user: User | undefined;
    try {
      const raw = localStorage.getItem(USER_KEY);
      user = raw ? (JSON.parse(raw) as User) : undefined;
    } catch {
      user = undefined;
    }

    return {
      success: true,
      token: outcome.token,
      refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY) || undefined,
      expiresIn,
      user,
    };
  },

  // 비밀번호 재설정 요청
  requestPasswordReset: async (payload: ResetPasswordPayload): Promise<void> => {
    await api.post<void>('/auth/request-password-reset', payload);
  },

  // 비밀번호 변경
  changePassword: async (payload: ChangePasswordPayload): Promise<void> => {
    await api.post<void>('/auth/change-password', payload);
  },

  // 인증 상태 확인
  isAuthenticated: (): boolean => {
    return !!getAuthToken();
  },

  // 세션 만료 시간 가져오기
  getSessionExpiry: (): Date | null => {
    return getSessionExpiry();
  },

  // 인증 번호 검증
  verifyCode: async (payload: VerifyCodePayload): Promise<VerifyCodeResponse> => {
    const { email, code } = payload;
    const response = await api.post<VerifyCodeResponse>('/auth/verify/code', { email, code });

    if (response.success && response.emailVerified) {
      setEmailVerification(email, true);
    }

    return response;
  },

  // 이메일 인증 상태 확인
  isEmailVerified: (email: string): boolean => {
    return getEmailVerification(email);
  },

  // 이메일 인증 코드 요청
  requestVerificationCode: async (
    email: string,
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await api.post<{ success: boolean; message?: string }>(
        '/auth/request-verification-code',
        { email },
      );
      return response;
    } catch (error) {
      console.error('이메일 인증 코드 요청 중 오류 발생:', error);
      return { success: false, message: '서버 통신 중 오류가 발생했습니다.' };
    }
  },
};

export default authService;
